import React from "react";

interface SimpleMarkdownProps {
  children: string;
  className?: string;
}

export const SimpleMarkdown: React.FC<SimpleMarkdownProps> = ({
  children,
  className,
}) => {
  const parseInlineMarkdown = (text: string): React.ReactNode[] => {
    // Handle empty or undefined text
    if (!text || typeof text !== "string") {
      return [text || ""];
    }

    const tokens: React.ReactNode[] = [];
    let currentIndex = 0;

    // Regex patterns for inline markdown
    const patterns = [
      { regex: /\*\*(.*?)\*\*/g, tag: "strong" }, // **bold**
      { regex: /\*(.*?)\*/g, tag: "em" }, // *italic*
      { regex: /_(.*?)_/g, tag: "em" }, // _italic_
      { regex: /`(.*?)`/g, tag: "code" }, // `code`
    ];

    // Find all matches and their positions
    const matches: Array<{
      start: number;
      end: number;
      content: string;
      tag: string;
      fullMatch: string;
    }> = [];

    patterns.forEach(({ regex, tag }) => {
      let match;
      const regexCopy = new RegExp(regex.source, regex.flags);

      while ((match = regexCopy.exec(text)) !== null) {
        // Ensure match and match[1] exist
        if (match && match[1] !== undefined) {
          matches.push({
            start: match.index,
            end: match.index + match[0].length,
            content: match[1],
            tag,
            fullMatch: match[0],
          });
        }
      }
    });

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep the first one)
    const filteredMatches = matches.filter((match, index) => {
      for (let i = 0; i < index; i++) {
        const prevMatch = matches[i];
        if (match.start < prevMatch.end) {
          return false;
        }
      }
      return true;
    });

    // Build the result
    filteredMatches.forEach((match, index) => {
      // Add text before this match
      if (match.start > currentIndex) {
        const textBefore = text.slice(currentIndex, match.start);
        if (textBefore) {
          tokens.push(textBefore);
        }
      }

      // Add the formatted element
      const key = `${match.tag}-${index}`;
      switch (match.tag) {
        case "strong":
          tokens.push(<strong key={key}>{match.content}</strong>);
          break;
        case "em":
          tokens.push(<em key={key}>{match.content}</em>);
          break;
        case "code":
          tokens.push(<code key={key}>{match.content}</code>);
          break;
        default:
          tokens.push(match.content);
      }

      currentIndex = match.end;
    });

    // Add remaining text
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex);
      if (remainingText) {
        tokens.push(remainingText);
      }
    }

    return tokens.length > 0 ? tokens : [text];
  };

  const parsedContent = parseInlineMarkdown(children);

  return <span className={className}>{parsedContent}</span>;
};
